import { useEffect, useState } from 'react'
import { useAppDispatch } from '../../reducers/store'
import {
  deactivateWebhook,
  fetchWebhook,
  fetchWebhookSecrets,
  saveWebhook,
  selectSettings,
} from '../../reducers/settings'
import { useSelector } from 'react-redux'
import { Button, Card, Flex, IconButton, Separator, Switch, Text } from '@radix-ui/themes'
import IntegrationModal from './IntegrationModal'
import toast from 'react-hot-toast'
import { SaveWebhookRequest, WebhookPayload, WebhookResponse, WebhookType } from '../../types/SettingsType'
import { Input } from '../input/Input'
import { selectUser } from '../../reducers/users'
import { RoleType } from '../../types/UserTypes'
import { getRoles, permitsRole } from '../../utils/roles'
import { CopyIcon, EyeNoneIcon, EyeOpenIcon } from '@radix-ui/react-icons'
import { DEFAULT_TOAST_CONFIG } from '../toast/consts'

function WebhookSettings() {
  const { webhooks, webhookSecrets } = useSelector(selectSettings)

  const [showGeneralWebhook, setShowGeneralWebhook] = useState<boolean>(false)
  const [showAlertWebhook, setShowAlertWebhook] = useState<boolean>(false)
  const [showAuditLogWebhook, setShowAuditLogWebhook] = useState<boolean>(false)

  const [generalWebhookURL, setGeneralWebhookURL] = useState<string>('')
  const [alertWebhookURL, setAlertWebhookURL] = useState<string>('')
  const [auditLogWebhookURL, setAuditLogWebhookURL] = useState<string>('')

  const [generalWebhookAuthHeader, setGeneralWebhookAuthHeader] = useState<string>('')
  const [alertWebhookAuthHeader, setAlertWebhookAuthHeader] = useState<string>('')
  const [auditLogWebhookAuthHeader, setAuditLogWebhookAuthHeader] = useState<string>('')

  const [showGeneralWebhookAuthHeader, setShowGeneralWebhookAuthHeader] = useState<boolean>(false)
  const [showAlertWebhookAuthHeader, setShowAlertWebhookAuthHeader] = useState<boolean>(false)
  const [showAuditLogWebhookAuthHeader, setShowAuditLogWebhookAuthHeader] = useState<boolean>(false)

  const [generalWebhookSecret, setGeneralWebhookSecret] = useState<string>('')
  const [alertWebhookSecret, setAlertWebhookSecret] = useState<string>('')
  const [auditLogWebhookSecret, setAuditLogWebhookSecret] = useState<string>('')

  const [showGeneralWebhookKey, setShowGeneralWebhookKey] = useState<boolean>(false)
  const [showAlertWebhookKey, setShowAlertWebhookKey] = useState<boolean>(false)
  const [showAuditLogWebhookKey, setShowAuditLogWebhookKey] = useState<boolean>(false)

  const [generalWebhookValidating, setGeneralWebhookValidating] = useState<boolean>(false)
  const [alertWebhookValidating, setAlertWebhookValidating] = useState<boolean>(false)
  const [auditLogWebhookValidating, setAuditLogWebhookValidating] = useState<boolean>(false)

  const [showRevokeModal, setShowRevokeModal] = useState<boolean>(false)
  const [webhookTypeToRemove, setWebhookTypeToRemove] = useState<WebhookType>(WebhookType.DEVICE_STATE)
  const [screenToShow, setScreenToShow] = useState<string>('REVOKE')

  const di = useAppDispatch()
  const { session, darkModeEnabled } = useSelector(selectUser)

  const roles = getRoles(session)
  const rolePermitsWebhookPolicy = permitsRole(roles, [RoleType.PLATFORM_MGMT])

  useEffect(() => {
    if (rolePermitsWebhookPolicy) {
      fetchWebhookData()
    }
    if (roles && !rolePermitsWebhookPolicy) {
      window.location.href = '/forbidden'
    }
  }, [session])

  const saveWebhookDetails = async (saveRequest: SaveWebhookRequest, setValidating: (value: boolean) => void) => {
    setValidating(true)
    const { authHeader } = saveRequest
    const headers = authHeader !== '' ? JSON.stringify({ Authorization: authHeader }) : undefined
    const request = {
      headers,
      secret: saveRequest.secret,
      url: saveRequest.url,
      webhookType: saveRequest.webhookType,
    }
    const response = (await di(saveWebhook(request))) as WebhookResponse
    if (!response.success) {
      toast.error(response.message, DEFAULT_TOAST_CONFIG)
    } else {
      toast.success('Webhook successfully saved', DEFAULT_TOAST_CONFIG)
      fetchWebhookData()
    }
    setValidating(false)
  }

  const fetchWebhookData = async () => {
    const webhooks = await di(fetchWebhook())
    if (webhooks?.length !== 3) {
      fetchMissingWebhookSecrets(webhooks ?? [])
    }
    webhooks?.map((webhook) => {
      if (webhook.webhookType === WebhookType.DEVICE_STATE) {
        setShowGeneralWebhook(true)
        setGeneralWebhookURL(webhook.url)
        setGeneralWebhookSecret(webhook.secret)
        setGeneralWebhookAuthHeader(webhook?.headers?.Authorization ?? '')
        if (webhook.headers?.Authorization) {
          setShowGeneralWebhookAuthHeader(true)
        }
      } else if (webhook.webhookType === WebhookType.ALERT) {
        setShowAlertWebhook(true)
        setAlertWebhookURL(webhook.url)
        setAlertWebhookSecret(webhook.secret)
        setAlertWebhookAuthHeader(webhook?.headers?.Authorization ?? '')
        if (webhook.headers?.Authorization) {
          setShowAlertWebhookAuthHeader(true)
        }
      } else if (webhook.webhookType === WebhookType.AUDIT_LOG) {
        setShowAuditLogWebhook(true)
        setAuditLogWebhookURL(webhook.url)
        setAuditLogWebhookSecret(webhook.secret)
        setAuditLogWebhookAuthHeader(webhook?.headers?.Authorization ?? '')
        if (webhook.headers?.Authorization) {
          setShowAuditLogWebhookAuthHeader(true)
        }
      }
    })
  }

  const fetchMissingWebhookSecrets = async (webhooks: WebhookPayload[]) => {
    const existingWebhookTypes = webhooks.map((webhook) => webhook.webhookType)
    const missingWebhookTypes = Object.values(WebhookType).filter((type) => !existingWebhookTypes.includes(type))

    await di(fetchWebhookSecrets(missingWebhookTypes))
  }

  const alertWebhookSecretFromSecrets = (type: WebhookType): string => {
    return webhookSecrets?.find((webhook) => webhook.webhookType === type)?.secret || ''
  }

  const toggleWebhook = (e: boolean, type: WebhookType) => {
    if (!e && webhooks) {
      if (type === WebhookType.DEVICE_STATE) {
        setShowGeneralWebhook(!showGeneralWebhook)
        setWebhookTypeToRemove(WebhookType.DEVICE_STATE)
      } else if (type === WebhookType.ALERT) {
        setShowAlertWebhook(!showAlertWebhook)
        setWebhookTypeToRemove(WebhookType.ALERT)
      } else if (type === WebhookType.AUDIT_LOG) {
        setShowAuditLogWebhook(!showAuditLogWebhook)
        setWebhookTypeToRemove(WebhookType.AUDIT_LOG)
      }
      setScreenToShow('WEBHOOK')
      setShowRevokeModal(true)
    } else {
      if (type === WebhookType.DEVICE_STATE) {
        setShowGeneralWebhook(!showGeneralWebhook)
      } else if (type === WebhookType.ALERT) {
        setShowAlertWebhook(!showAlertWebhook)
      } else if (type === WebhookType.AUDIT_LOG) {
        setShowAuditLogWebhook(!showAuditLogWebhook)
      }
    }
  }

  const callDeactivateWebhook = async (type: WebhookType) => {
    await di(deactivateWebhook(type))
    if (type === WebhookType.DEVICE_STATE) {
      setGeneralWebhookURL('')
      setGeneralWebhookAuthHeader('')
    } else if (type === WebhookType.ALERT) {
      setAlertWebhookURL('')
      setAlertWebhookAuthHeader('')
    } else if (type === WebhookType.AUDIT_LOG) {
      setAuditLogWebhookURL('')
      setAuditLogWebhookAuthHeader('')
    }
    di(fetchWebhook())
    setShowRevokeModal(false)
    toast.success('Webhook successfully deactivated', DEFAULT_TOAST_CONFIG)
  }

  const cancelDeactivateWebhook = () => {
    if (webhookTypeToRemove === WebhookType.DEVICE_STATE) {
      setShowGeneralWebhook(!showGeneralWebhook)
    } else if (webhookTypeToRemove === WebhookType.ALERT) {
      setShowAlertWebhook(!showAlertWebhook)
    } else if (webhookTypeToRemove === WebhookType.AUDIT_LOG) {
      setShowAuditLogWebhook(!showAuditLogWebhook)
    }
  }

  return (
    <>
      <div className="w-full h-[calc(100vh_-_80px)] overflow-auto mt-6">
        {/* Webhook settings */}
        {rolePermitsWebhookPolicy && (
          <Flex direction="column" gap="3" maxWidth="100%">
            <Card variant="surface">
              <Flex justify="between">
                <div className="block">
                  <Text as="div" size="2" weight="bold">
                    Device State Webhook
                  </Text>
                  <Text as="div" size="1" className="text-neutral-11 dark:text-neutral-9">
                    The device state webhook is triggered when a change has been detected on the device such as OS
                    version, biometrics or because of an insecure scan.
                  </Text>
                </div>
                <Switch
                  size="2"
                  className={`${darkModeEnabled ? 'dark' : 'light'}`}
                  checked={showGeneralWebhook}
                  onCheckedChange={(e) => toggleWebhook(e, WebhookType.DEVICE_STATE)}
                />
              </Flex>
              {showGeneralWebhook && (
                <>
                  <Separator my="3" size="4" className="dark:bg-gray-500" />
                  <div className="flex mt-2 justify-between">
                    <div className="w-full mr-4">
                      <Text as="div" size="1" className="mt-2 mb-1 text-neutral-11 dark:text-neutral-9">
                        Webhook URL
                      </Text>
                      <Input type="text" value={generalWebhookURL} onChange={setGeneralWebhookURL} />
                    </div>
                    <Button
                      variant="outline"
                      size="3"
                      className={`h-9 w-[200px] border border-mainPurple bg-transparent text-mainPurple cursor-pointer text-sm font-semibold mt-7 mr-2`}
                      onClick={() => {
                        setShowGeneralWebhookAuthHeader(!showGeneralWebhookAuthHeader)
                      }}
                      disabled={generalWebhookValidating}
                    >
                      {showGeneralWebhookAuthHeader ? 'Hide Advanced Options' : 'Show Advanced Options'}
                    </Button>
                    <Button
                      variant="solid"
                      size="3"
                      className={`h-9 w-[200px] bg-mainPurple hover:bg-tertiaryPurple active:active:bg-tertiaryPurple text-white cursor-pointer text-sm font-semibold mt-7`}
                      onClick={() =>
                        saveWebhookDetails(
                          {
                            authHeader: generalWebhookAuthHeader,
                            secret: generalWebhookSecret || alertWebhookSecretFromSecrets(WebhookType.DEVICE_STATE),
                            url: generalWebhookURL,
                            webhookType: WebhookType.DEVICE_STATE,
                          },
                          setGeneralWebhookValidating
                        )
                      }
                      disabled={generalWebhookValidating}
                    >
                      {generalWebhookValidating ? 'Validating...' : 'Save'}
                    </Button>
                  </div>
                  {showGeneralWebhookAuthHeader && (
                    <>
                      <Separator my="4" size="4" />
                      <div className="flex">
                        <div className="w-1/2 mr-4">
                          <Text as="div" size="1" className="text-secondaryText">
                            Webhook Secret
                          </Text>
                          <div className="h-[38px] flex mt-1 border-mainBorder border rounded px-2 py-2 justify-between text-sm">
                            {showGeneralWebhookKey
                              ? generalWebhookSecret || alertWebhookSecretFromSecrets(WebhookType.DEVICE_STATE)
                              : '*********************'}
                            <div className="flex mt-0.5">
                              <IconButton
                                size="2"
                                variant="ghost"
                                className="mr-1"
                                onClick={() => setShowGeneralWebhookKey(!showGeneralWebhookKey)}
                              >
                                {showGeneralWebhookKey ? (
                                  <EyeNoneIcon height="16" width="16" />
                                ) : (
                                  <EyeOpenIcon height="16" width="16" />
                                )}
                              </IconButton>
                              <IconButton
                                size="2"
                                variant="ghost"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    generalWebhookSecret || alertWebhookSecretFromSecrets(WebhookType.DEVICE_STATE)
                                  )
                                  toast.success('API Key copied to clipboard', DEFAULT_TOAST_CONFIG)
                                }}
                              >
                                <CopyIcon height="16" width="16" />
                              </IconButton>
                            </div>
                          </div>
                        </div>
                        <div className="w-1/2 mr-4">
                          <Text as="div" size="1" className="mb-1 text-secondaryText">
                            Authorization
                          </Text>
                          <Input
                            type="text"
                            value={generalWebhookAuthHeader}
                            onChange={setGeneralWebhookAuthHeader}
                            onFocus={() => {
                              if (generalWebhookAuthHeader.startsWith('****************************')) {
                                setGeneralWebhookAuthHeader('')
                              }
                            }}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </Card>
            <Card variant="surface">
              <Flex justify="between">
                <div className="block">
                  <Text as="div" size="2" weight="bold">
                    Alert Webhook
                  </Text>
                  <Text as="div" size="1" className="text-neutral-11 dark:text-neutral-9">
                    The alert webhook is triggered when a device scan creates an alert. Duplicate scans will be included
                    but will contain an updated 'last seen at' value.
                  </Text>
                </div>
                <Switch
                  size="2"
                  className={`${darkModeEnabled ? 'dark' : 'light'}`}
                  checked={showAlertWebhook}
                  onCheckedChange={(e) => toggleWebhook(e, WebhookType.ALERT)}
                />
              </Flex>
              {showAlertWebhook && (
                <>
                  <Separator my="3" size="4" className="dark:bg-gray-500" />
                  <div className="flex mt-2 justify-between">
                    <div className="w-full mr-4">
                      <Text as="div" size="1" className="mt-2 mb-1 text-neutral-11 dark:text-neutral-9">
                        Webhook URL
                      </Text>
                      <Input type="text" value={alertWebhookURL} onChange={setAlertWebhookURL} />
                    </div>
                    <Button
                      variant="outline"
                      size="3"
                      className={`h-9 w-[200px] border border-mainPurple bg-transparent text-mainPurple cursor-pointer text-sm font-semibold mt-7 mr-2`}
                      onClick={() => {
                        setShowAlertWebhookAuthHeader(!showAlertWebhookAuthHeader)
                      }}
                      disabled={alertWebhookValidating}
                    >
                      {showAlertWebhookAuthHeader ? 'Hide Advanced Options' : 'Show Advanced Options'}
                    </Button>
                    <Button
                      variant="solid"
                      size="3"
                      className={`h-9 w-[200px] bg-mainPurple hover:bg-tertiaryPurple active:active:bg-tertiaryPurple text-white cursor-pointer text-sm font-semibold mt-7`}
                      onClick={() =>
                        saveWebhookDetails(
                          {
                            authHeader: alertWebhookAuthHeader,
                            secret: alertWebhookSecret || alertWebhookSecretFromSecrets(WebhookType.ALERT),
                            url: alertWebhookURL,
                            webhookType: WebhookType.ALERT,
                          },
                          setAlertWebhookValidating
                        )
                      }
                      disabled={alertWebhookValidating}
                    >
                      {alertWebhookValidating ? 'Validating...' : 'Save'}
                    </Button>
                  </div>
                  {showAlertWebhookAuthHeader && (
                    <>
                      <Separator my="4" size="4" />
                      <div className="flex">
                        <div className="w-1/2 mr-4">
                          <Text as="div" size="1" className="text-secondaryText">
                            Webhook Secret
                          </Text>
                          <div className="h-[38px] flex mt-1 border-mainBorder border rounded px-2 py-2 justify-between text-sm">
                            {showAlertWebhookKey
                              ? alertWebhookSecret || alertWebhookSecretFromSecrets(WebhookType.ALERT)
                              : '*********************'}
                            <div className="flex mt-0.5">
                              <IconButton
                                size="2"
                                variant="ghost"
                                className="mr-1"
                                onClick={() => setShowAlertWebhookKey(!showAlertWebhookKey)}
                              >
                                {showAlertWebhookKey ? (
                                  <EyeNoneIcon height="16" width="16" />
                                ) : (
                                  <EyeOpenIcon height="16" width="16" />
                                )}
                              </IconButton>
                              <IconButton
                                size="2"
                                variant="ghost"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    alertWebhookSecret || alertWebhookSecretFromSecrets(WebhookType.ALERT)
                                  )
                                  toast.success('API Key copied to clipboard', DEFAULT_TOAST_CONFIG)
                                }}
                              >
                                <CopyIcon height="16" width="16" />
                              </IconButton>
                            </div>
                          </div>
                        </div>
                        <div className="w-1/2 mr-4">
                          <Text as="div" size="1" className="mb-1 text-secondaryText">
                            Authorization
                          </Text>
                          <Input
                            type="text"
                            value={alertWebhookAuthHeader}
                            onChange={setAlertWebhookAuthHeader}
                            onFocus={() => {
                              if (alertWebhookAuthHeader.startsWith('****************************')) {
                                setAlertWebhookAuthHeader('')
                              }
                            }}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </Card>
            <Card variant="surface">
              <Flex justify="between">
                <div className="block">
                  <Text as="div" size="2" weight="bold">
                    Audit Log Webhook
                  </Text>
                  <Text as="div" size="1" className="text-neutral-11 dark:text-neutral-9">
                    The audit log webhook is triggered when any admin action within the portal is performed. This
                    includes creating, updating or deleting devices, users, roles, etc.
                  </Text>
                </div>
                <Switch
                  size="2"
                  className={`${darkModeEnabled ? 'dark' : 'light'}`}
                  checked={showAuditLogWebhook}
                  onCheckedChange={(e) => toggleWebhook(e, WebhookType.AUDIT_LOG)}
                />
              </Flex>
              {showAuditLogWebhook && (
                <>
                  <Separator my="3" size="4" className="dark:bg-gray-500" />
                  <div className="flex mt-2 justify-between">
                    <div className="w-full mr-4">
                      <Text as="div" size="1" className="mt-2 mb-1 text-neutral-11 dark:text-neutral-9">
                        Webhook URL
                      </Text>
                      <Input type="text" value={auditLogWebhookURL} onChange={setAuditLogWebhookURL} />
                    </div>
                    <Button
                      variant="outline"
                      size="3"
                      className={`h-9 w-[200px] border border-mainPurple bg-transparent text-mainPurple cursor-pointer text-sm font-semibold mt-7 mr-2`}
                      onClick={() => {
                        setShowAuditLogWebhookAuthHeader(!showAuditLogWebhookAuthHeader)
                      }}
                      disabled={auditLogWebhookValidating}
                    >
                      {showAuditLogWebhookAuthHeader ? 'Hide Advanced Options' : 'Show Advanced Options'}
                    </Button>
                    <Button
                      variant="solid"
                      size="3"
                      className={`h-9 w-[200px] bg-mainPurple hover:bg-tertiaryPurple active:active:bg-tertiaryPurple text-white cursor-pointer text-sm font-semibold mt-7`}
                      onClick={() =>
                        saveWebhookDetails(
                          {
                            authHeader: auditLogWebhookAuthHeader,
                            secret: auditLogWebhookSecret || alertWebhookSecretFromSecrets(WebhookType.AUDIT_LOG),
                            url: auditLogWebhookURL,
                            webhookType: WebhookType.AUDIT_LOG,
                          },
                          setAuditLogWebhookValidating
                        )
                      }
                      disabled={auditLogWebhookValidating}
                    >
                      {auditLogWebhookValidating ? 'Validating...' : 'Save'}
                    </Button>
                  </div>
                  {showAuditLogWebhookAuthHeader && (
                    <>
                      <Separator my="4" size="4" />
                      <div className="flex">
                        <div className="w-1/2 mr-4">
                          <Text as="div" size="1" className="text-secondaryText">
                            Webhook Secret
                          </Text>
                          <div className="h-[38px] flex mt-1 border-mainBorder border rounded px-2 py-2 justify-between text-sm">
                            {showAuditLogWebhookKey
                              ? auditLogWebhookSecret || alertWebhookSecretFromSecrets(WebhookType.AUDIT_LOG)
                              : '*********************'}
                            <div className="flex mt-0.5">
                              <IconButton
                                size="2"
                                variant="ghost"
                                className="mr-1"
                                onClick={() => setShowAuditLogWebhookKey(!showAuditLogWebhookKey)}
                              >
                                {showAuditLogWebhookKey ? (
                                  <EyeNoneIcon height="16" width="16" />
                                ) : (
                                  <EyeOpenIcon height="16" width="16" />
                                )}
                              </IconButton>
                              <IconButton
                                size="2"
                                variant="ghost"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    auditLogWebhookSecret || alertWebhookSecretFromSecrets(WebhookType.AUDIT_LOG)
                                  )
                                  toast.success('API Key copied to clipboard', DEFAULT_TOAST_CONFIG)
                                }}
                              >
                                <CopyIcon height="16" width="16" />
                              </IconButton>
                            </div>
                          </div>
                        </div>
                        <div className="w-1/2 mr-4">
                          <Text as="div" size="1" className="mb-1 text-secondaryText">
                            Authorization
                          </Text>
                          <Input
                            type="text"
                            value={auditLogWebhookAuthHeader}
                            onChange={setAuditLogWebhookAuthHeader}
                            onFocus={() => {
                              if (auditLogWebhookAuthHeader.startsWith('****************************')) {
                                setAuditLogWebhookAuthHeader('')
                              }
                            }}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </Card>
            <Text as="div" size="2" className="mt-4">
              Read{' '}
              <span className="text-mainPurple dark:text-secondaryPurple cursor-pointer">
                <a href={`${window.location.origin}/docs/iVerify-developer-guide/iVerify-webhooks/`} target="_blank">
                  Webhooks Setup Documentation
                </a>
              </span>
            </Text>
          </Flex>
        )}
      </div>
      {showRevokeModal && (
        <IntegrationModal
          setShowIntegrationModal={setShowRevokeModal}
          screenToShow={screenToShow}
          deactivateWebhook={callDeactivateWebhook}
          cancelDeactivateWebhook={cancelDeactivateWebhook}
          webhookType={webhookTypeToRemove}
        />
      )}
    </>
  )
}

export default WebhookSettings
